import React, { useState, useEffect } from 'react';
import { Database, ShieldCheck, RefreshCw, Activity, Terminal, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';

interface DbTableStatus {
  name: string;
  count: number;
  status: string;
}

interface AuditLogEntry {
  id: string;
  userId: string;
  action: string;
  previousValue: string;
  newValue: string;
  timestamp: string;
}

interface AdminDbAuditProps {
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const AdminDbAudit: React.FC<AdminDbAuditProps> = ({ onAddLog }) => {
  const { user } = useAuth();
  const [tables, setTables] = useState<DbTableStatus[]>([]);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [dbInfo, setDbInfo] = useState<{ dbEngine: string; path: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const idToken = await user.getIdToken();
      const headers = {
        'Authorization': `Bearer ${idToken}`,
        'X-CSRF-Token': document.cookie.match(/(^|;)\s*csrf_token_secret\s*=\s*([^;]+)/)?.[2] || ''
      };

      // 1. Fetch DB Status
      const statusRes = await fetch('/api/admin/product-builder/db-status', { headers });
      const statusData = await statusRes.json();

      if (statusData.success) {
        setTables(statusData.tables);
        setDbInfo({ dbEngine: statusData.dbEngine, path: statusData.path });
      } else {
        throw new Error(statusData.error || 'Failed to fetch database status.');
      }

      // 2. Fetch Audit Logs
      const logsRes = await fetch('/api/admin/product-builder/audit-logs', { headers });
      const logsData = await logsRes.json();

      if (logsData.success) {
        setLogs(logsData.logs || []);
      } else {
        throw new Error(logsData.error || 'Failed to fetch audit records.');
      }

      onAddLog('Product Admin', 'Refreshed Database schema tables and security audit trails', 'SUCCESS', 'Executed secure SQL status and audit logs recovery.');
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Retrieval error: Unauthorized or database connectivity offline.');
      onAddLog('Product Admin', 'Failed to read SQLite tables and audit trails', 'DENIED', err.message || '');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const getStatusColor = (status: string) => {
    if (status === 'Active') return 'text-[#00ffff] bg-[#00ffff]/10 border-[#00ffff]/20';
    return 'text-rose-400 bg-rose-950/40 border-rose-900';
  };

  return (
    <div className="space-y-6 text-left animate-fade-in font-mono">
      {/* HEADER CONTROLS */}
      <div className="border-b border-white/5 pb-4 flex flex-wrap justify-between items-center gap-4">
        <div>
          <div className="flex items-center gap-1.5">
            <Database className="w-4 h-4 text-[#00ffff]" />
            <h2 className="text-sm tracking-widest text-[#00ffff] uppercase font-black font-sans">Admin Database & Security Auditor</h2>
          </div>
          <p className="text-xs text-zinc-400 mt-1">Real-time status overview of active SQLite3 schemas, row telemetry, and direct pricing configuration audit logs.</p>
        </div>

        <button
          onClick={fetchData}
          disabled={loading}
          className="px-3.5 py-2 bg-zinc-900 border border-white/10 hover:bg-zinc-850 hover:border-white/20 active:bg-zinc-800 text-white text-[10px] font-bold rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-[#00ffff]' : 'text-zinc-400 group-hover:text-white'}`} />
          <span>{loading ? 'Refreshing...' : 'Query Sync'}</span>
        </button>
      </div>

      {error && (
        <div className="p-3.5 bg-rose-950/40 rounded-xl border border-rose-500/20 flex gap-2.5 items-start text-rose-200">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider">Access Blocked / Database Error</p>
            <p className="text-[10px] text-rose-300 leading-normal normal-case mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* SECTION 1: MASTER SCHEMA TELEMETRY MAP */}
      <div className="bg-zinc-950/60 p-4 border border-white/5 rounded-2xl space-y-4 font-sans">
        <div className="flex flex-wrap justify-between items-center border-b border-white/5 pb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#00ffff]" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider font-mono">Active Schema Table Integrity</span>
          </div>
          {dbInfo && (
            <div className="text-[9px] text-zinc-550 flex items-center gap-3 font-mono">
              <span>ENGINE: <span className="text-zinc-400 font-bold">{dbInfo.dbEngine}</span></span>
              <span className="truncate max-w-xs">PATH: <span className="text-zinc-400 font-bold">{dbInfo.path}</span></span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 font-mono">
          {tables.map((table) => (
            <div
              key={table.name}
              className="p-3 bg-[#0c1024]/40 rounded-xl border border-white/5 flex flex-col justify-between space-y-2.5 transition-all hover:border-cyan-500/15"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-[10.5px] font-bold text-white truncate" title={table.name}>
                  {table.name}
                </span>
                <span className={`text-[7px] uppercase font-black px-1.5 py-0.5 rounded border ${getStatusColor(table.status)} shrink-0`}>
                  {table.status}
                </span>
              </div>

              <div className="flex justify-between items-end border-t border-white/3 pt-2">
                <span className="text-[8.5px] text-zinc-500 uppercase">Live Records</span>
                <span className="text-[11px] font-black text-white bg-zinc-900 border border-white/5 px-2 py-0.5 rounded">
                  {table.count} <span className="text-[7.5px] text-zinc-500 font-normal">rows</span>
                </span>
              </div>
            </div>
          ))}
          {tables.length === 0 && !loading && (
            <div className="col-span-full py-8 text-center text-zinc-550 text-xs font-mono">
              No schema records loaded. Run Query Sync.
            </div>
          )}
        </div>
      </div>

      {/* SECTION 2: AUDIT LOG LEDGER COMPONENT */}
      <div className="bg-zinc-950/60 p-4 border border-white/5 rounded-2xl space-y-4">
        <div className="flex items-center justify-between border-b border-white/5 pb-3 font-sans">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-[#00ffff]" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider font-mono">Change Management Audit Ledger (product_builder_audit_log)</span>
          </div>
          <span className="text-[8.5px] text-zinc-500 font-black font-mono">
            Total Captured: <span className="text-[#00ffff] bg-zinc-900 border border-white/5 px-2 py-0.5 rounded">{logs.length}</span>
          </span>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/5 bg-black/30">
          <table className="w-full text-left text-[10px] border-collapse font-sans">
            <thead>
              <tr className="bg-[#030616] border-b border-white/5 text-zinc-400 font-bold uppercase tracking-wider text-[8px] font-mono">
                <th className="p-3">Reference/ID</th>
                <th className="p-3">Timestamp (Local)</th>
                <th className="p-3">Operator (Admin USER ID)</th>
                <th className="p-3">Action Description</th>
                <th className="p-3">Previous Value (Old)</th>
                <th className="p-3">New Value (New)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/2 font-mono">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-white/1 cursor-default transition-colors text-slate-300">
                  <td className="p-3 font-bold text-zinc-500 text-[9px]">
                    {log.id}
                  </td>
                  <td className="p-3 text-[9.5px]">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                  </td>
                  <td className="p-3 text-cyan-400 text-[9.5px] truncate max-w-[125px]" title={log.userId}>
                    {log.userId}
                  </td>
                  <td className="p-3 text-white text-[10px] font-sans">
                    {log.action}
                  </td>
                  <td className="p-3 text-amber-500/80 font-bold text-right text-[11px]">
                    {log.previousValue}
                  </td>
                  <td className="p-3 text-emerald-400 font-black text-right text-[11px]">
                    {log.newValue}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-zinc-550 italic font-mono text-xs">
                    No change records found. Only pricing config updates are parsed.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
